/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {ExclamationTriangleIcon} from '@heroicons/react/20/solid';
import type {AxiosError} from 'axios';
import {toast} from 'react-toastify';
import useLocales from '@/hooks/useLocales';
import useError from '@/hooks/useError';
import LoadingScreen from '@/components/common/LoadingScreen';
import {createFormFields, updateFormFields} from '@/apis/FormFields';
import {
  createPositionFields,
  updatePositionFields
} from '@/apis/PositionFields';

type Requirement = 'OFF' | 'OPTIONAL' | 'REQUIRED';

type OptionConfig = {
  key: string;
  label: string;
  follow_up_title: string;
  comment: Requirement;
  photos: Requirement;
  documents: Requirement;
  is_active: boolean;
  saved?: boolean;
};

const REQUIREMENTS: Requirement[] = ['OFF', 'OPTIONAL', 'REQUIRED'];
const FOLLOW_UP_KINDS = ['comment', 'photos', 'documents'] as const;
const DOCUMENT_TYPES = ['PDF', 'WORD', 'EXCEL', 'POWERPOINT', 'JPEG', 'PNG'];

/** The 'Voertuig in orde?' example: Ja, Nee (with follow-up) and N.v.t. */
export const DEFAULT_OPTIONS: OptionConfig[] = [
  {
    key: 'yes',
    label: 'Ja',
    follow_up_title: '',
    comment: 'OFF',
    photos: 'OFF',
    documents: 'OFF',
    is_active: true
  },
  {
    key: 'no',
    label: 'Nee',
    follow_up_title: 'Actie nodig vanuit bedrijf?',
    comment: 'REQUIRED',
    photos: 'OPTIONAL',
    documents: 'OPTIONAL',
    is_active: true
  },
  {
    key: 'na',
    label: 'N.v.t.',
    follow_up_title: '',
    comment: 'OFF',
    photos: 'OFF',
    documents: 'OFF',
    is_active: true
  }
];

/** Make a stable option key from a label, unique among the given keys. */
export const makeOptionKey = (label: string, existing: string[]) => {
  const base =
    label
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'option';
  let key = base;
  let counter = 2;
  while (existing.includes(key)) {
    key = `${base}_${counter}`;
    counter += 1;
  }
  return key;
};

/**
 * Settings of a 'Meerkeuze met toelichting en bijlagen' field.
 *
 * Per answer the administrator chooses whether a comment, photos and documents
 * are off, optional or required, and which follow-up text is shown. The
 * behaviour is linked to the option key, which never changes after saving.
 */
const NewChoiceFollowUpField = (props: any) => {
  const {translate} = useLocales();
  const {handleErrors} = useError();
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  // the answers are always shown as buttons; a saved display type is kept
  const [displayType, setDisplayType] = React.useState('BUTTONS');
  const [options, setOptions] = React.useState<OptionConfig[]>(DEFAULT_OPTIONS);
  const [documentTypes, setDocumentTypes] = React.useState<string[]>([
    'PDF',
    'WORD',
    'EXCEL'
  ]);
  const [maxFileSize, setMaxFileSize] = React.useState(10);
  const [maxPhotos, setMaxPhotos] = React.useState(10);
  const [maxDocuments, setMaxDocuments] = React.useState(10);
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const isPositionField =
    props?.type === 'position_field' ||
    window.location.pathname.includes('new_position_field');

  React.useEffect(() => {
    if (props?.id) {
      setLabel(props.label ?? '');
      setDescription(props.description ?? '');
      setDisplayType(props.display_type ?? 'BUTTONS');
      setOptions(
        (props.options ?? []).map((o: OptionConfig) => ({...o, saved: true}))
      );
      setDocumentTypes(props.document_types ?? []);
      setMaxFileSize(props.max_file_size ?? 10);
      setMaxPhotos(props.max_photos ?? 10);
      setMaxDocuments(props.max_documents ?? 10);
    } else {
      setLabel('');
      setDescription('');
      setOptions(DEFAULT_OPTIONS);
    }
  }, [props?.id]);

  const updateOption = (index: number, changes: Partial<OptionConfig>) =>
    setOptions(prev =>
      prev.map((option, i) => (i === index ? {...option, ...changes} : option))
    );

  const addOption = () =>
    setOptions(prev => [
      ...prev,
      {
        key: '',
        label: '',
        follow_up_title: '',
        comment: 'OFF',
        photos: 'OFF',
        documents: 'OFF',
        is_active: true
      }
    ]);

  const removeOption = (index: number) =>
    setOptions(prev => prev.filter((_, i) => i !== index));

  const documentsUsed = options.some(o => o.is_active && o.documents !== 'OFF');
  const invalid =
    !label.trim() ||
    options.filter(o => o.is_active).length === 0 ||
    options.some(o => !o.label.trim()) ||
    (documentsUsed && documentTypes.length === 0);

  const submitField = () => {
    setSubmitted(true);
    props?.setRestoreFieldName?.('');
    if (invalid) {
      return;
    }
    const keys: string[] = [];
    const payloadOptions = options.map(option => {
      const key = option.key || makeOptionKey(option.label, keys);
      keys.push(key);
      const {saved, ...rest} = option;
      return {...rest, key};
    });
    const payload: any = {
      field_type: 'choice_follow_up',
      label,
      description,
      placeholder: '',
      display_type: displayType,
      options: payloadOptions,
      document_types: documentTypes,
      max_file_size: maxFileSize,
      max_photos: maxPhotos,
      max_documents: maxDocuments
    };
    const request = props?.id
      ? (isPositionField ? updatePositionFields : updateFormFields)({
          ...payload,
          id: props.id
        })
      : (isPositionField ? createPositionFields : createFormFields)(payload);

    setLoading(true);
    request
      .then(() => {
        setLoading(false);
        setSubmitted(false);
        props?.setUpdated?.(true);
        toast.success(translate('submitted_success'));
      })
      .catch((error: AxiosError<any>) => {
        setLoading(false);
        if (
          error?.response?.data?.non_field_errors?.[0] ===
          'deleted_field_with_provided_label_already_exists'
        ) {
          props?.setRestoreFieldName?.(label);
          toast.error(translate('field_already_exist'));
        } else {
          handleErrors(error?.response?.data);
        }
      });
  };

  const requirementSelect = (
    index: number,
    kind: (typeof FOLLOW_UP_KINDS)[number]
  ) => (
    <Select
      size="small"
      value={options[index][kind]}
      className="bg-white min-w-[120px]"
      data-testid={`follow-up-${kind}-${index}`}
      onChange={event =>
        updateOption(index, {[kind]: event.target.value as Requirement})
      }
    >
      {REQUIREMENTS.map(requirement => (
        <MenuItem key={requirement} value={requirement}>
          {translate(`follow_up_${requirement.toLowerCase()}`)}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <div className="flex flex-col justify-between min-h-[70vh] gap-6">
      <div className="mt-5 flex flex-col gap-5 overflow-y-auto">
        <FormControl className="w-[300px]">
          <FormLabel className="text-[#212B36] mb-1">
            {translate('Label')}
          </FormLabel>
          <TextField
            size="small"
            value={label}
            inputProps={{'data-testid': 'follow-up-field-label'}}
            onChange={event => setLabel(event.target.value)}
            placeholder={translate('Label')}
            error={submitted && !label.trim()}
            helperText={
              submitted && !label.trim() ? translate('required') : null
            }
          />
        </FormControl>
        <FormControl className="w-full max-w-[600px]">
          <FormLabel className="text-[#212B36] mb-1">
            {translate('follow_up_instruction')}
          </FormLabel>
          <TextField
            size="small"
            multiline
            minRows={2}
            value={description}
            onChange={event => setDescription(event.target.value)}
          />
        </FormControl>
        <div>
          <p className="text-[16px] mb-2">{translate('follow_up_options')}</p>
          <p className="text-[12px] text-gray-500 mb-3">
            {translate('follow_up_options_help')}
          </p>
          <div className="flex flex-col gap-3">
            {options.map((option, index) => (
              <div
                key={option.key || `new-${index}`}
                data-testid="follow-up-option-config"
                className={`rounded-lg border p-3 flex flex-col gap-3 ${
                  option.is_active
                    ? 'border-gray-200'
                    : 'border-dashed opacity-60'
                }`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <TextField
                    size="small"
                    label={translate('follow_up_option_label')}
                    value={option.label}
                    inputProps={{
                      'data-testid': `follow-up-option-label-${index}`
                    }}
                    onChange={event =>
                      updateOption(index, {label: event.target.value})
                    }
                    error={submitted && !option.label.trim()}
                  />
                  <TextField
                    size="small"
                    className="min-w-[260px]"
                    label={translate('follow_up_title')}
                    value={option.follow_up_title}
                    onChange={event =>
                      updateOption(index, {follow_up_title: event.target.value})
                    }
                  />
                  {option.saved ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={option.is_active}
                          onChange={event =>
                            updateOption(index, {
                              is_active: event.target.checked
                            })
                          }
                        />
                      }
                      label={translate('follow_up_option_active')}
                    />
                  ) : (
                    <IconButton
                      aria-label={translate('follow_up_remove_option')}
                      onClick={() => removeOption(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  {FOLLOW_UP_KINDS.map(kind => (
                    <div key={kind} className="flex items-center gap-2">
                      <span className="text-sm">
                        {translate(`follow_up_${kind}`)}
                      </span>
                      {requirementSelect(index, kind)}
                    </div>
                  ))}
                </div>
                {option.saved && (
                  <span className="text-[11px] text-gray-400">
                    {translate('follow_up_option_key')}: {option.key}
                  </span>
                )}
              </div>
            ))}
          </div>
          <Button
            onClick={addOption}
            className="font-open-sans normal-case underline pl-0 mt-2"
          >
            + {translate('follow_up_add_option')}
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-[16px]">{translate('follow_up_file_rules')}</p>
          <div>
            <p className="text-sm mb-1">
              {translate('follow_up_document_types')}
            </p>
            <div className="flex flex-wrap">
              {DOCUMENT_TYPES.map(type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={documentTypes.includes(type)}
                      onChange={event =>
                        setDocumentTypes(prev =>
                          event.target.checked
                            ? [...prev, type]
                            : prev.filter(t => t !== type)
                        )
                      }
                    />
                  }
                  label={type}
                />
              ))}
            </div>
            {submitted && documentsUsed && documentTypes.length === 0 && (
              <span className="text-error text-sm">
                {translate('follow_up_document_types_required')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <TextField
              size="small"
              type="number"
              label={translate('follow_up_max_file_size')}
              value={maxFileSize}
              inputProps={{min: 1}}
              onChange={event => setMaxFileSize(Number(event.target.value))}
            />
            <TextField
              size="small"
              type="number"
              label={translate('follow_up_max_photos')}
              value={maxPhotos}
              inputProps={{min: 1}}
              onChange={event => setMaxPhotos(Number(event.target.value))}
            />
            <TextField
              size="small"
              type="number"
              label={translate('follow_up_max_documents')}
              value={maxDocuments}
              inputProps={{min: 1}}
              onChange={event => setMaxDocuments(Number(event.target.value))}
            />
          </div>
        </div>
      </div>
      {loading && <LoadingScreen />}
      <div className="flex">
        {props?.is_standard && (
          <div className="flex">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-dark-yellow"
              aria-hidden="true"
            />
            <span className="text-dark-yellow">
              {translate('not_editable')}
            </span>
          </div>
        )}
        <Button
          variant="contained"
          size="small"
          color="inherit"
          data-testid="follow-up-field-save"
          className="bg-[#144A6C] mr-5 px-7 h-[20px] text-white normal-case"
          onClick={submitField}
          disabled={loading}
        >
          {translate('save')}
        </Button>
      </div>
    </div>
  );
};

export default NewChoiceFollowUpField;
